import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, Clock, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [cashRef, setCashRef] = useState({ id: "", reference: "" });

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/billing/invoices", { params }).then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const collected = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  const pending = invoices.filter((i) => i.status === "pending").reduce((s, i) => s + i.amount, 0);

  const recordCashApp = async () => {
    try {
      await api.post(`/billing/invoices/${cashRef.id}/cashapp`, { reference: cashRef.reference });
      toast.success("CashApp payment recorded");
      setCashRef({ id: "", reference: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Billing</h1>
          <p className="text-sm text-slate-500">PayPal charges run in the client portal. CashApp payments are logged manually here.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-emerald-600" />
          <div><div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Collected</div>
          <div className="text-xl font-bold font-heading text-slate-900" data-testid="billing-collected">{money(collected)}</div></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <Clock className="h-8 w-8 text-amber-600" />
          <div><div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Outstanding</div>
          <div className="text-xl font-bold font-heading text-slate-900" data-testid="billing-outstanding">{money(pending)}</div></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <CreditCard className="h-8 w-8 text-slate-500" />
          <div><div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Invoices</div>
          <div className="text-xl font-bold font-heading text-slate-900">{invoices.length}</div></div>
        </CardContent></Card>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {["Client", "Invoice", "Amount", "Status", "Method", "Reference", "Date", ""].map((h) => (
                <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                <td className="py-2.5 px-3.5">
                  <Link to={`/admin/clients/${i.client_id}`} className="text-blue-700 hover:underline font-medium" data-testid={`invoice-client-${i.id.slice(0, 8)}`}>{i.client_name}</Link>
                </td>
                <td className="py-2.5 px-3.5 text-slate-700">{i.label}</td>
                <td className="py-2.5 px-3.5 text-slate-700">{money(i.amount)}</td>
                <td className="py-2.5 px-3.5"><StatusChip status={i.status} testId="invoice-status-pill" /></td>
                <td className="py-2.5 px-3.5 text-slate-600 capitalize">{i.method || "—"}</td>
                <td className="py-2.5 px-3.5 text-xs font-mono text-slate-500">{i.reference || "—"}</td>
                <td className="py-2.5 px-3.5 text-slate-500">{fmtDate(i.created_at)}</td>
                <td className="py-2.5 px-3.5">
                  {i.status === "pending" && (
                    cashRef.id === i.id ? (
                      <div className="flex gap-1.5">
                        <Input placeholder="$Cashtag / ref" value={cashRef.reference} onChange={(e) => setCashRef({ ...cashRef, reference: e.target.value })} className="h-8 w-36 text-xs" data-testid="cashapp-ref-input" />
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={recordCashApp} data-testid="cashapp-confirm-btn">Save</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setCashRef({ id: i.id, reference: "" })} data-testid="record-cashapp-btn">Record CashApp</Button>
                    )
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
